import React, { useContext, useState, useRef } from "react";
import Navbar from "../components/Navbar";
import { ThemeContext } from "../contexts/ThemeContext";
import { Box, Stack, Typography, Button } from "@mui/material";
import SocialMediaSidebar from "../components/SocialMediaSidebar";
import { motion, AnimatePresence, Variants, useReducedMotion } from "framer-motion";
import Footer from "../components/Footer";
import { MdExpandLess, MdExpandMore } from "react-icons/md";
import { brandPalette } from "../theme/brand";

// képek importálása
import StarImage from "../assets/images/logo.jpeg";
import s1 from "../assets/images/s1.png";
import s2 from "../assets/images/s2.png";
import s3 from "../assets/images/s3.png";
import s4 from "../assets/images/s4.png";
import s5 from "../assets/images/s5.png";
import s6 from "../assets/images/s6.png";

/**
 * Framer-motion Variants (TS-kompatibilis cubic-bezier 'ease' használattal)
 */
const collapseVariants: Variants = {
  collapsed: {
    height: 0,
    opacity: 0,
    transition: { duration: 0.35, ease: [0.4, 0.0, 0.2, 1] },
  },
  expanded: {
    height: "auto",
    opacity: 1,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

const innerTextVariants: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] },
  },
  exit: { opacity: 0, y: 6, transition: { duration: 0.35, ease: [0.4, 0.0, 0.2, 1] } },
};

const MI: React.FC = () => {
  const { darkMode } = useContext(ThemeContext)!;
  const pal = brandPalette(darkMode);
  const shouldReduceMotion = useReducedMotion();
  const firstSectionRef = useRef<HTMLDivElement>(null);

  const [expanded, setExpanded] = useState<number | null>(null);
  const handleExpandClick = (index: number) => {
    setExpanded((prev) => (prev === index ? null : index));
  };

  const handleScrollToContent = () => {
    if (firstSectionRef.current) {
      firstSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const sections = [
    {
      title: "Mi az a mesterséges intelligencia? 🤖",
      short: "Az MI olyan gépek és algoritmusok összessége, amelyek képesek tanulni és alkalmazkodni emberi gondolkodáshoz hasonló módon.",
      full: "A mesterséges intelligencia (MI) a számítástudomány egy olyan ága, amely intelligens gépek létrehozására összpontosít, amelyek képesek gondolkodni, tanulni és cselekedni az emberi agyhoz hasonló módon. Ezek a rendszerek hatalmas mennyiségű adat feldolgozásával képesek mintázatokat felismerni és döntéseket hozni, anélkül, hogy minden egyes lépést expliciten beprogramoztak volna bennük. A mai MI rendszerek, mint a nagy nyelvi modellek, hihetetlenül összetett feladatokat képesek elvégezni, például szövegeket generálni, képeket elemezni vagy akár diagnosztikai segítséget nyújtani. Az MI fő célja, hogy automatizálja a kognitív feladatokat, ezzel időt és erőforrást takarítva meg az emberek számára. Képesek alkalmazkodni új környezethez és folyamatosan fejlődnek a kapott visszajelzések alapján. Az MI rendszereket egyre inkább használják kutatásban, fejlesztésben és a mindennapi életben is. Az MI folyamatosan formálja a jövőt, ahogy egyre intelligensebb és önállóbb rendszereket hozunk létre.",
      bg: darkMode ? "rgba(29,18,71,0.88)" : "#FFFFFF",
      direction: "left",
      image: s1,
    },
    {
      title: "Az MI rövid története 📜",
      short: "Az MI alapjai az 1950-es évekre nyúlnak vissza, Alan Turing és a Dartmouth konferencia munkáira.",
      full: 'A mesterséges intelligencia gyökerei az ókori filozófiába nyúlnak vissza, de a modern kutatások az 1940-es és 1950-es években kezdődtek. Alan Turing, a számítástechnika atyja, 1950-ben publikálta „Számítógépek és intelligencia” című cikkét, amely felvetette a kérdést, hogy képesek-e a gépek gondolkodni, és bevezette az azóta híressé vált Turing-tesztet. Az MI kifejezést először 1956-ban, a Dartmouthi nyári kutatókonferencián használták. Az 1960-as és 70-es években az MI-kutatás felvirágzott, ám az ígéretes kezdetek után egy úgynevezett "MI-tél" következett az 1980-as években a kutatási támogatások csökkenése miatt. A 2000-es években a számítási teljesítmény növekedése és a hatalmas adathalmazok elérhetősége új lendületet adott a területnek. A mélytanulási algoritmusok és a neurális hálózatok fejlődésével az MI ismét a figyelem középpontjába került, és ma már a technológia egyik legfontosabb hajtóerejévé vált.',
      bg: darkMode ? "rgba(62,22,96,0.82)" : "#FBF3FD",
      direction: "right",
      image: s2,
    },
    {
      title: "Hogyan működnek az algoritmusok? 🧠",
      short: "Az MI algoritmusok adatokból tanulnak, felismerik a mintázatokat és előrejelzéseket készítenek.",
      full: "Az MI algoritmusok működése leginkább az emberi agy tanulási folyamatához hasonlítható. Ahelyett, hogy egyértelmű utasításokat kapnának, ezek az algoritmusok hatalmas mennyiségű, úgynevezett tréning adatok segítségével tanulnak. A folyamat során az algoritmusok mintázatokat és összefüggéseket keresnek az adatokban. Ha például képeket kapnak, megtanulják, hogyan néz ki egy macska vagy egy kutya, anélkül, hogy minden egyes tulajdonságát beprogramozták volna. A tanítási fázis után az algoritmusok képesek előrejelzéseket és döntéseket hozni új, sosem látott adatok alapján. Ez a folyamat iteratív: a modell folyamatosan finomítja a tudását a visszajelzések alapján, hogy a következő előrejelzések még pontosabbak legyenek. A gépi tanulás különböző típusai (pl. felügyelt, felügyelet nélküli és megerősítéses tanulás) mind ezen az alapelven nyugszanak. Ez a folyamat teszi lehetővé, hogy az MI rendszerek képesek legyenek egyre bonyolultabb feladatokat megoldani.",
      bg: darkMode ? "rgba(29,18,71,0.88)" : "#FFFFFF",
      direction: "left",
      image: s3,
    },
    {
      title: "Hétköznapi alkalmazások 🚗",
      short: "Streaming ajánlók, virtuális asszisztensek, önvezető autók és arcfelismerés – mind MI alapú.",
      full: "A mesterséges intelligencia már szerves része a mindennapjainknak, gyakran anélkül, hogy észrevennénk. Az olyan streaming szolgáltatások, mint a Netflix vagy a Spotify, ajánlórendszereket használnak, hogy személyre szabott tartalmat javasoljanak nekünk a korábbi megtekintéseink vagy hallgatásaink alapján. A virtuális asszisztensek (például Siri, Google Assistant és Alexa) segítségével hangutasításokkal vezérelhetjük okostelefonjainkat és otthoni eszközeinket. Az önvezető autók rendszere szintén MI-re épül, amely valós időben dolgozza fel a szenzorok adatait, hogy a jármű biztonságosan navigáljon az utakon. Az okostelefonok arcfelismerő funkciója is MI-technológiát használ. A modern orvosi képalkotásban az MI segít az orvosoknak a diagnózis felállításában, a mintázatok gyors felismerésével. Ezen kívül, a spamek szűrése az e-mail fiókokban, és az online csalások felismerése is MI alapú algoritmusokkal történik. Ezek az alkalmazások mind azt mutatják, hogy az MI a felhasználói élményt és a hatékonyságot is növeli.",
      bg: darkMode ? "rgba(62,22,96,0.82)" : "#FBF3FD",
      direction: "right",
      image: s4,
    },
    {
      title: "Előnyök és kockázatok ⚖️",
      short: "Az MI forradalmasítja az ipart és az egészségügyet, de etikai dilemmákat is felvet.",
      full: "A mesterséges intelligencia számos előnnyel jár, amelyek forradalmasíthatják a társadalmat. Az egészségügyben segíthet a betegségek korai diagnosztizálásában, a gyógyszerkutatás felgyorsításában és a betegellátás javításában. Az iparban az MI növeli a termelékenységet, minimalizálja a hibákat és automatizálja a veszélyes munkaköröket. Az oktatásban személyre szabott tanulási útvonalakat kínálhat a diákok számára. Ugyanakkor az MI komoly kockázatokat és etikai kérdéseket is felvet. A munkahelyi átalakulás az egyik legnagyobb aggodalom, mivel egyes munkakörök automatizálhatók. Emellett a bias (előítélet) problémája is fennáll, mivel az algoritmusok tükrözhetik az adathalmazokban lévő emberi előítéleteket. A magánélet védelmére és az adatok felhasználására vonatkozó aggodalmak szintén kulcsfontosságúak. Az MI-technológia felelőtlen felhasználása komoly társadalmi és etikai dilemmákat okozhat.",
      bg: darkMode ? "rgba(29,18,71,0.88)" : "#FFFFFF",
      direction: "left",
      image: s5,
    },
    {
      title: "A jövő lehetőségei 🚀",
      short: "A kvantumszámítógépek és erős MI rendszerek új szintre emelhetik a tudományt.",
      full: "A mesterséges intelligencia jövője tele van izgalmas lehetőségekkel. Az új generációs algoritmusok és a kvantumszámítógépek várható fejlődése révén az MI-rendszerek soha nem látott mértékben növelhetik a számítási teljesítményt. Ez lehetővé teheti, hogy az MI olyan komplex problémák megoldásában vegyen részt, mint a globális klímaváltozás modellezése, új fenntartható anyagok felfedezése, vagy a rákgyógyszerek kutatásának felgyorsítása. Az erős MI (AGI) fejlesztése, amely képes lenne a humán szintű kognitív képességekkel rendelkezni, egy régóta dédelgetett álom, ami forradalmasíthatja az összes tudományos területet. A személyre szabott orvoslásban az MI pontosabb kezelési terveket javasolhat a genetikai adatok elemzésével. Végül, a jövőben az MI a tudományos felfedezések motorja lehet, és hozzájárulhat a világegyetem megértéséhez is.",
      bg: darkMode ? "rgba(62,22,96,0.82)" : "#FBF3FD",
      direction: "right",
      image: s6,
    },
  ];

  const logoAnimate = shouldReduceMotion
  ? ({ y: 0 } as any)
  : ({ y: [0, -6, 0] } as any);

  const logoTransition = shouldReduceMotion
  ? ({ duration: 0 } as any)
  : ({ duration: 4, repeat: Infinity, ease: [0.22, 1, 0.36, 1] } as any);

  return (
    <>
      <Navbar />
      <SocialMediaSidebar />

      <Box className={`page-container ${darkMode ? "dark" : "light"}`}>
        {/* Hero Section */}
        <Box
          component="section"
          sx={{
            minHeight: "90vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: { xs: 2, md: 8 },
            py: 6,
            flexDirection: { xs: "column", md: "row" },
            background: "transparent",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ width: "100%", padding: "24px 8px" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 3,
                flexDirection: { xs: "column", md: "row" },
                mb: 3,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: pal.heading,
                    fontSize: { xs: "2rem", sm: "2.8rem", md: "3.75rem" },
                    lineHeight: 1.15,
                  }}
                >
                  Fedezd fel a mesterséges intelligenciát
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: pal.text,
                    mb: 4,
                    maxWidth: 700,
                    fontSize: { xs: "1.05rem", md: "1.25rem" },
                  }}
                >
                  Ismerd meg, hogyan alakítja át az MI a világunkat – a tudománytól a
                  mindennapi életig.
                </Typography>
                
                <Stack direction={{ xs: "column", sm: "row" }} spacing={3} sx={{ mt: 2 }}>
                  {/* Modernizált Fő Gomb (Kezdjünk el) */}
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleScrollToContent}
                    sx={{
                      background: 'linear-gradient(45deg, #C92BB5, #6D1B77)',
                      borderRadius: '30px', // Kapszula forma
                      color: '#fff',
                      fontWeight: 'bold',
                      textTransform: 'none', // Ne legyen csupa nagybetű
                      fontSize: '1.1rem',
                      padding: '12px 32px',
                      boxShadow: '0 8px 15px rgba(201, 43, 181, 0.35)',
                      transition: 'all 0.3s ease',
                      "&:hover": { 
                        background: 'linear-gradient(45deg, #6D1B77, #C92BB5)',
                        transform: "translateY(-3px)", // Felfelé mozdul
                        boxShadow: '0 12px 20px rgba(201, 43, 181, 0.5)',
                      },
                    }}
                  >
                    Kezdjünk el
                  </Button>
                  
                  {/* Modernizált Másodlagos Gomb (Facebook) */}
                  <Button
                    variant="outlined"
                    size="large"
                    href="https://www.facebook.com/groups/mikezdoknek"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      borderRadius: '30px',
                      fontWeight: 'bold',
                      textTransform: 'none',
                      fontSize: '1.1rem',
                      padding: '12px 32px',
                      border: '2px solid',
                      borderColor: pal.accent,
                      color: darkMode ? '#fff' : '#6D1B77',
                      transition: 'all 0.3s ease',
                      "&:hover": { 
                        background: darkMode ? 'rgba(252, 198, 50, 0.15)' : 'rgba(109, 27, 119, 0.08)',
                        borderColor: pal.accent,
                        color: pal.heading,
                        transform: "translateY(-3px)",
                        boxShadow: '0 8px 15px rgba(201, 43, 181, 0.2)',
                      },
                    }}
                  >
                    Látogass el a Facebook oldalunkra!
                  </Button>
                </Stack>
              </Box>

              {/* Logó */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                  alignItems: "center",
                  ml: { md: 4 },
                  mt: { xs: 2, md: 0 },
                }}
              >
                <motion.img
                  src={StarImage}
                  alt="Csillagtanoda logó"
                  initial={{ y: 0 }}
                  animate={logoAnimate}
                  transition={logoTransition}
                  style={{
                    width: 540,
                    maxWidth: "min(240px, 60vw)",
                    height: "auto",
                    borderRadius: 8,
                    objectFit: "cover",
                  }}
                  aria-hidden={false}
                />
              </Box>
            </Box>
          </motion.div>
        </Box>

        {/* Content Sections */}
        <Box component="section" sx={{ px: { xs: 2, md: 8 }, py: 8 }}>
          <Stack spacing={6}>
            {sections.map((section, i) => (
              <motion.div
                key={i}
                ref={i === 0 ? firstSectionRef : null}
                initial={{ opacity: 0, x: section.direction === "left" ? -40 : 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                viewport={{ once: true }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: {
                      xs: "column",
                      md: section.direction === "left" ? "row" : "row-reverse",
                    },
                    alignItems: "center",
                    p: { xs: 2.5, md: 4 },
                    borderRadius: 3,
                    background: section.bg,
                    border: pal.cardBorder,
                    boxShadow: pal.cardShadow,
                  }}
                >
                  <Box sx={{ flex: 1, px: { xs: 0, md: 3 }, mb: { xs: 3, md: 0 } }}>
                    <Typography
                      variant="h4"
                      sx={{
                        mb: 2,
                        color: pal.heading,
                        fontWeight: 700,
                        fontSize: { xs: "1.4rem", md: "2.125rem" },
                      }}
                    >
                      {section.title}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{ color: pal.text, mb: 2 }}
                    >
                      {section.short}
                    </Typography>

                    <AnimatePresence initial={false}>
                      {expanded === i && (
                        <motion.div
                          key={`expand-${i}`}
                          initial="collapsed"
                          animate="expanded"
                          exit="collapsed"
                          variants={collapseVariants}
                          style={{ overflow: "hidden" }}
                          id={`section-content-${i}`}
                          aria-live="polite"
                        >
                          <motion.div
                            variants={innerTextVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: pal.muted,
                                mt: 1,
                                lineHeight: 1.7,
                              }}
                            >
                              {section.full}
                            </Typography>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {/* Modernizált Dinamikus Tartalom Gomb */}
                    <Button
                      variant={expanded === i ? "text" : "contained"}
                      onClick={() => handleExpandClick(i)}
                      startIcon={expanded === i ? <MdExpandLess /> : <MdExpandMore />}
                      sx={{
                        mt: 3,
                        borderRadius: '25px', // Kapszula forma a tartalom gomboknak is
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: '8px 24px',
                        // Dinamikus háttér: ha nyitva van, szolidabb legyen
                        background: expanded === i 
                          ? (darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)')
                          : 'linear-gradient(45deg, #6D1B77, #C92BB5)', // Arculati lila átmenet
                        color: expanded === i 
                          ? (darkMode ? '#ccc' : '#444')
                          : '#fff',
                        boxShadow: expanded === i ? 'none' : '0 4px 15px rgba(201, 43, 181, 0.4)',
                        transition: 'all 0.3s ease',
                        "&:hover": {
                          background: expanded === i 
                            ? (darkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.1)')
                            : 'linear-gradient(45deg, #C92BB5, #6D1B77)',
                          transform: expanded === i ? 'none' : 'translateY(-2px)',
                          boxShadow: expanded === i ? 'none' : '0 6px 20px rgba(201, 43, 181, 0.6)',
                        }
                      }}
                      aria-expanded={expanded === i}
                      aria-controls={`section-content-${i}`}
                    >
                      {expanded === i ? "Összecsukás" : "Tovább olvasom"}
                    </Button>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={section.image}
                      alt={section.title}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "350px",
                        borderRadius: "12px",
                        boxShadow: darkMode
                          ? "0 0 12px rgba(255,255,255,0.12)"
                          : "0 8px 18px rgba(2,6,23,0.08)",
                      }}
                    />
                  </Box>
                </Box>
              </motion.div>
            ))}
          </Stack>
        </Box>
      </Box>

      <Footer />
    </>
  );
};

export default MI;